import type { InvoiceTimelineResponse } from './types';

import {
  type QueryFunctionContext,
  queryOptions,
  type UseSuspenseQueryOptions,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { ms } from 'ms';

import type { UUID } from '~/types/common';

import { queryKeysInvoice } from '../queryKeys';

import { fetchInvoiceTimeline } from './fetchInvoiceTimeline';

type InvoiceTimelineQueryOptions = Omit<
  UseSuspenseQueryOptions<InvoiceTimelineResponse>,
  'queryFn' | 'queryKey'
>;

/**
 * Generates query options for the invoice timeline endpoint.
 *
 * @param invoiceId - Invoice identifier.
 * @param options - Additional suspense-query options.
 * @returns Query options for the invoice timeline.
 */
// fallow-ignore-next-line unused-export -- canonical query-options API for route loaders
export function getInvoiceTimelineQueryOptions(
  invoiceId: UUID,
  options?: InvoiceTimelineQueryOptions,
) {
  return queryOptions({
    queryFn: (context: QueryFunctionContext) =>
      fetchInvoiceTimeline(invoiceId, context),
    queryKey: queryKeysInvoice.getTimeline(invoiceId),
    staleTime: ms('60s'),
    ...options,
  });
}

/**
 * Loads the audit timeline (system events and comments) of an invoice.
 *
 * @param invoiceId - Invoice identifier.
 * @param options - Additional suspense-query options.
 * @returns Suspense query result containing the timeline.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/get_invoice_timeline_invoices__invoice_id__timeline_get
 */
export function useQueryInvoiceTimeline(
  invoiceId: UUID,
  options?: InvoiceTimelineQueryOptions,
) {
  return useSuspenseQuery(getInvoiceTimelineQueryOptions(invoiceId, options));
}
